/**
 * PlpBannerManager — custom element that re-renders the product-list via AJAX
 * whenever the desktop grid layout changes (user toggle) or the viewport WIDTH
 * crosses a column-count breakpoint, so PLP banner positions and sides are
 * always correct without a full page refresh.
 *
 * Scroll flicker fix: we track only window.innerWidth via a 'resize' listener
 * (not ResizeObserver on body, which fires on mobile as the address bar
 * shows/hides during scroll and changes body height).
 *
 * Usage (injected by main-collection.liquid):
 *   <plp-banner-manager
 *     section-id="<section.id>"
 *     product-list-id="product-list-<section.id>"
 *   ></plp-banner-manager>
 */

// Breakpoints that change effective column count — mirrors main-collection.liquid CSS.
const BREAKPOINTS = [700, 1000, 1400];

// Mobile-only banner cadence (independent of desktop slot math): 1st banner after the
// 2nd product, then every 10 products after that (2, 12, 22, 32...). Desktop DOM order
// (rendered server-side by main-collection.liquid) is left untouched — we only reorder
// the already-rendered banner nodes for the mobile viewport.
const MOBILE_BANNER_FIRST = 2;
const MOBILE_BANNER_INTERVAL = 10;

class PlpBannerManager extends HTMLElement {
  connectedCallback() {
    this._abortController = null;
    this._layoutObserver = null;
    this._lastBreakpointBucket = this._breakpointBucket();
    this._onResize = this._handleResize.bind(this);

    this._attachLayoutObserver(this._productList());
    this._applyMobileLayout(this._productList());
    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    this._layoutObserver?.disconnect();
    this._abortController?.abort();
    window.removeEventListener('resize', this._onResize);
  }

  // ── private ──────────────────────────────────────────────────────────────

  _productList() {
    return document.getElementById(this.getAttribute('product-list-id'));
  }

  _sectionId() {
    return this.getAttribute('section-id');
  }

  /** Which breakpoint bucket are we in? Returns the highest breakpoint ≤ innerWidth. */
  _breakpointBucket() {
    const w = window.innerWidth;
    let bucket = 0;
    for (const bp of BREAKPOINTS) {
      if (w >= bp) bucket = bp;
    }
    return bucket;
  }

  _handleResize() {
    clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => {
      const bucket = this._breakpointBucket();
      if (bucket === this._lastBreakpointBucket) return;
      this._lastBreakpointBucket = bucket;
      const list = this._productList();
      const layout = list?.getAttribute('collection-desktop-layout') || 'medium';
      // No cart save needed — layout mode unchanged, only viewport changed.
      this._reRender(layout, { saveCart: false });
    }, 200);
  }

  _attachLayoutObserver(node) {
    if (!node) return;
    this._layoutObserver?.disconnect();
    this._layoutObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'collection-desktop-layout') {
          this._reRender(node.getAttribute('collection-desktop-layout'));
          break;
        }
      }
    });
    this._layoutObserver.observe(node, { attributes: true });
  }

  async _reRender(layout, { saveCart = true } = {}) {
    this._abortController?.abort();
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    try {
      if (saveCart) {
        await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { products_desktop_grid_mode: layout } }),
          signal,
        });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this._sectionId());
      const html = await (await fetch(url.toString(), { signal })).text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newList = doc.getElementById(this.getAttribute('product-list-id'));
      if (!newList) return;

      // Preserve the layout attribute the grid-switch JS already applied.
      newList.setAttribute('collection-desktop-layout', layout);

      // Swap — old list stays visible until this exact line (no flicker).
      const current = this._productList();
      if (current) {
        current.replaceWith(newList);
        this._attachLayoutObserver(newList);
        this._applyMobileLayout(newList);
      }
    } catch (e) {
      if (e.name !== 'AbortError') console.error('[plp-banner-manager]', e);
    }
  }

  /**
   * Mobile only (<700px): move the already-rendered banner nodes (server placed them at
   * desktop slots) so they instead land after the 2nd product, then every 10 products
   * after that (2, 12, 22, 32...). No-op on desktop — the server-rendered order is used as-is.
   */
  _applyMobileLayout(list) {
    if (!list || this._breakpointBucket() !== 0) return;

    const banners = Array.from(list.querySelectorAll('.product-list__promo'));
    if (!banners.length) return;

    banners.forEach((banner) => banner.remove());

    const products = Array.from(list.querySelectorAll('product-card'));
    let target = MOBILE_BANNER_FIRST;

    for (const banner of banners) {
      const anchor = products[target - 1];
      if (!anchor) break;
      anchor.after(banner);
      target += MOBILE_BANNER_INTERVAL;
    }
  }
}

if (!window.customElements.get('plp-banner-manager')) {
  customElements.define('plp-banner-manager', PlpBannerManager);
}
